/**
 * V2, words as things (docs/ideas/loom-v2.md, phase 4): the pocket. Above the writing line, what you carry ("On you")
 * and the names you pocketed from a look closer; tap one and its name goes into your line, with either pen. A look
 * closer gains "Pocket it" and "Write with it". What the story has taken away since (someone else has it, it was
 * destroyed, the place is out of reach) stays in the pocket, struck through, and says why instead of writing.
 * The pocket is kept on the server (shared/story.ts pocketOf, pocketAfter); pocket.css has the shared look.
 */
import { useState } from "react";
import type { PocketItem } from "../../shared/story";
import type { StoryUI } from "../useStory";
import { Tap } from "./pens";
import "./pocket.css";

const usable = (p: PocketItem) => !p.state;
/** Why it can't be written with, as a sentence (the ledger's facts come without a full stop). */
const why = (p: PocketItem) => (p.note ?? `${p.name} is gone now`).replace(/[.!?]?\s*$/, ".");

/** The pocket, above the line: one row of names, scrolling sideways when it's full. */
export function PocketBar({ s }: { s: StoryUI }) {
  const pocket = s.view?.pocket ?? [];
  const [shown, setShown] = useState<PocketItem | null>(null);
  if (!s.id || !pocket.length || s.draft.trimStart().startsWith("/")) return null;
  return (
    <div className="v2-pocket">
      <p className="v2-pocket-row" aria-label="Your pocket: tap a name to write with it">
        <span className="v2-pocket-h" aria-hidden="true">pocket</span>
        {pocket.map((p) => (
          <span key={`${p.on}:${p.id}`} className={`v2-chip v2-chip-${p.on}${p.state ? ` v2-chip-${p.state}` : ""}`}>
            <Tap className="v2-chip-name" label={usable(p) ? `Write with ${p.name}${p.on === "you" ? " (on you)" : ""}` : `${p.name}: ${why(p)}`}
              onTap={() => (usable(p) ? (setShown(null), s.writeWith(p.name)) : setShown(shown?.id === p.id ? null : p))}>
              {p.on === "you" && <span className="v2-chip-mark" aria-hidden="true">◦ </span>}{p.name}
            </Tap>
            {p.on === "word" && <Tap className="v2-chip-x" label={`Take ${p.name} out of your pocket`} onTap={() => { if (shown?.id === p.id) setShown(null); s.pocket(p.id, "out"); }}>×</Tap>}
          </span>
        ))}
      </p>
      {shown && <p className="v2-pocket-why" role="status">{why(shown)} You can't write with it now.</p>}
    </div>
  );
}

/** Under a look closer: keep the name in your pocket, or write with it now. */
export function LookActions({ s, thing, className = "" }: { s: StoryUI; thing: string; className?: string }) {
  if (!s.id || !s.view) return null;
  const name = s.view.things.find((t) => t.id === thing)?.name ?? thing;
  const held = s.view.pocket.find((p) => p.id === thing);
  return (
    <p className={`v2-look-acts ${className}`}>
      {held?.on === "you" ? <span className="v2-look-held">on you</span>
        : held ? <><span className="v2-look-held">in your pocket</span> <Tap className="v2-look-act v2-look-quiet" onTap={() => s.pocket(thing, "out")}>take out</Tap></>
        : <Tap className="v2-look-act" label={`Pocket ${name}: keep the name to write with later`} onTap={() => s.pocket(thing, "in")}>Pocket it</Tap>}
      {held?.state ? <span className="v2-look-why">{why(held)}</span>
        : <Tap className="v2-look-act" label={`Write with ${name}: put it in your line`} onTap={() => s.writeWith(name)}>Write with it</Tap>}
    </p>
  );
}
